import React from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

// Primitivas de tabla del admin — Tailwind (utilities-only) sobre las CSS variables
// del tema, así light/dark siguen andando. Primer uso: tabla del módulo Mayorista.
// La idea es ir migrando las tablas del admin a estas piezas en vez de estilos inline.

export const cn = (...args: clsx.ClassValue[]) => twMerge(clsx(args));

export function TableCard({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn('overflow-x-auto rounded-xl border border-line bg-card shadow-sm', className)}>
      {children}
    </div>
  );
}

export function Table({ className, children }: { className?: string; children: React.ReactNode }) {
  return <table className={cn('w-full border-collapse text-[0.85rem] text-ink', className)}>{children}</table>;
}

export function THead({ children }: { children: React.ReactNode }) {
  return <thead className="sticky top-0 z-[1] bg-card">{children}</thead>;
}

export function Th({ className, children, align = 'left' }: { className?: string; children?: React.ReactNode; align?: 'left' | 'right' | 'center' }) {
  return (
    <th className={cn(
      'border-b-2 border-line px-3 py-2 font-mono text-[0.66rem] font-semibold uppercase tracking-wider text-faint',
      align === 'right' && 'text-right', align === 'center' && 'text-center', align === 'left' && 'text-left',
      className,
    )}>{children}</th>
  );
}

export function Tr({ className, children, dim }: { className?: string; children: React.ReactNode; dim?: boolean }) {
  return (
    <tr className={cn(
      'border-t border-line transition-colors hover:bg-[color-mix(in_srgb,var(--border)_18%,transparent)]',
      dim && 'opacity-45',
      className,
    )}>{children}</tr>
  );
}

export function Td({ className, children, align = 'left', title }: { className?: string; children?: React.ReactNode; align?: 'left' | 'right' | 'center'; title?: string }) {
  return (
    <td title={title} className={cn(
      'px-3 py-[7px] align-top',
      align === 'right' && 'text-right tabular-nums', align === 'center' && 'text-center',
      className,
    )}>{children}</td>
  );
}

// Pill de estado (semáforo y similares)
export function Pill({ tone, title, children }: { tone: 'green' | 'yellow' | 'red' | 'neutral'; title?: string; children: React.ReactNode }) {
  const tones: Record<string, string> = {
    green: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    yellow: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    red: 'bg-red-500/15 text-red-700 dark:text-red-400',
    neutral: 'bg-[color-mix(in_oklab,var(--border)_40%,transparent)] text-soft',  // /40 sobre var() no genera regla
  };
  return (
    <span title={title} className={cn('inline-block rounded-lg px-2.5 py-1 text-right leading-tight', tones[tone])}>
      {children}
    </span>
  );
}
